import { useMemo, useState } from "react"
import { networkLabel } from "src/shared/enums/networkColors"
import { NO_VALUE, count } from "../../../performanceSnapshot"

/*
    Audience Sizing and Matched By Network (MPN-1530 §5.1, §5.2, §5.3).

    The segmented control switches BOTH the sizing cards and the table, per §5.1. Every
    audience arrives in one payload, so switching is a re-render rather than a refetch.

    This deliberately reverses an earlier decision. MonitorDashboard and useMonitorRoute
    both said "no audience picker here" -- an audience-scoped view was arrived at
    pre-filtered from the campaign table. The ticket asks for the picker, and it earns its
    place on this tab specifically: comparing how three audiences matched is the question
    the Audience tab exists to answer.

    On the three numbers: Available is the population the audience draws from, Selected is
    who was picked into it, Matched is what the networks confirmed. They were previously
    identical because one query counted one thing three times.
*/

type NetworkRow = {
  network: string
  selected: number
  matched: number | null
  match_rate: number | null
}

type Audience = {
  id: number | null
  name: string
  available: number
  selected: number
  matched: number | null
  /** Selected members no longer in the current population cut, so the page can explain
      why Selected can exceed Available instead of the pair looking broken. */
  selected_outside_population?: number
  networks: NetworkRow[]
}

function Card({ value, label, hint, accent }: {
  value: string
  label: string
  hint: string
  accent?: boolean
}) {
  return (
    <div className={`mon-kpi${accent ? " mon-kpi--accent" : ""}`}>
      <div className="mon-kpi__value">{value}</div>
      <div className="mon-kpi__label">
        {label}
        <span className="mon-kpi__note">{hint}</span>
      </div>
    </div>
  )
}

export default function AudienceBreakdown({ data }: { data: any }) {
  const breakdown = data?.["audience-breakdown"] ?? {}
  const audiences: Audience[] = breakdown.audiences ?? []
  const all: Audience | null = breakdown.all ?? null

  // null means "All Audiences", matching the server's own id for the blended row.
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const current = useMemo(() => {
    if (selectedId === null) return all
    return audiences.find((a) => a.id === selectedId) ?? all
  }, [selectedId, audiences, all])

  if (!current) {
    return <p className="text-muted fs-11 mb-0">This campaign has no audiences yet.</p>
  }

  const options: Audience[] = all ? [all, ...audiences] : audiences

  const contractListSize: number | null = breakdown.contract_list_size ?? null
  const sizedBy: string = breakdown.contract_sized_by ?? ""
  const sizedByLabel =
    sizedBy === "CAMPAIGN" ? "per campaign · " : sizedBy === "CONTRACT" ? "per contract · " : ""

  /*
      The audiences behind the All Audiences figure, for the working shown inside the
      disclosure. An audience no network has answered for yet contributes nothing to the
      answer, so it is left out rather than listed as a dash that means nothing here.
  */
  const audiencesWithCounts = audiences.filter((a) => a.matched !== null)
  const bestAudienceMatched = audiencesWithCounts.length
    ? Math.max(...audiencesWithCounts.map((a) => a.matched as number))
    : null

  return (
    <div>
      {/* One option per audience plus All Audiences (§5.1). */}
      {options.length > 1 && (
        <div className="mon-grain mon-grain--wide">
          {options.map((audience) => (
            <button
              key={audience.id ?? "all"}
              type="button"
              className={`mon-grain__btn${
                (audience.id ?? null) === selectedId ? " mon-grain__btn--on" : ""
              }`}
              onClick={() => setSelectedId(audience.id ?? null)}
            >
              {audience.name}
            </button>
          ))}
        </div>
      )}

      <div className="mon-panel__cards">
        <Card
          value={count(current.available)}
          label="Available"
          hint="people in the population"
        />
        <Card
          value={count(current.selected)}
          label="Selected"
          /*
              When the population has been re-cut since this audience was built, Selected
              can exceed Available and the pair looks broken. The hint says why rather
              than the numbers being quietly reconciled: padding Available up to Selected
              would read as "we targeted the whole population", which is false, and the
              ratio of the two is the entire reason they sit side by side.
          */
          hint={
            current.selected_outside_population
              ? `${count(current.selected_outside_population)} no longer in the population`
              : "chosen into this audience"
          }
        />
        <Card
          /*
              null means no network has reported a count, which is not the same as zero
              and must not render as one. Rachel read "Matched 0" against a live Meta
              campaign as "nothing matched"; nothing had asked.

              The trailing + is not decoration. This is a floor, not a headcount: the
              networks return totals and never identities, so people matching on two of
              them cannot be told apart from people matching on one. Rachel asked for
              unique users here, and a bare number would read as exactly that.
          */
          value={current.matched === null ? NO_VALUE : `${count(current.matched)}+`}
          label="Matched"
          hint="at least this many, on one or more networks"
          accent
        />
        {/*
            The contracted ceiling (MPN-1718). Reference only -- the ticket is explicit
            that exceeding it triggers no block, alert or rejection, so the hint says so
            rather than leaving a bare number beside three measured ones to be read as a
            limit the platform is holding the audience to.

            Absent when no ceiling is recorded, or when the scope spans two contracts.
            A dash here would be a fourth number that looks measured and is not.
        */}
        {contractListSize != null && (
          <Card
            value={count(contractListSize)}
            label="Contract list size"
            hint={`${sizedByLabel}reference only`}
          />
        )}
      </div>

      <p className="mon-panel__foot">
        Available is this audience's own population as it stands today. A population is
        rebuilt on every file load, so an audience built earlier can hold people who have
        since dropped out of it &mdash; when that happens, Selected reads higher than
        Available and the count beneath it says how many. The + on Matched means at least:
        more people matched than the number shown, but the networks do not tell us enough
        to count them exactly.
      </p>

      {/*
          Collapsed on purpose. This is the fourth round of corrections on this panel and
          each one has wanted another sentence of explanation; left visible they
          accumulate until the page explains itself more than it shows anything. The +
          and the hint under the card carry the meaning on their own, so this is the
          reasoning for whoever wants it rather than something everybody has to read.

          Plain <details> to match NetworkViewPanel, which is how this app already does
          progressive disclosure.
      */}
      <details className="mon-panel__why">
        <summary>Why this is not an exact number</summary>
        <p className="mon-panel__foot">
          Each network tells us how many of the contacts we sent it recognised, but never
          which ones. So when two networks both report a match, we have no way of knowing
          whether that is two different people or the same person twice. That is why the
          numbers cannot simply be added up.
        </p>
        <p className="mon-panel__foot">
          What we show is the best result any single network returned, so the real number
          is that or higher, and no higher than the rows below added together. It is safe
          to quote as "at least this many".
        </p>

        {/*
            The working, rather than a description of it. "Where does 2,000 come from"
            was the first question this panel got asked, twice, and a sentence saying
            "the largest audience" does not answer it the way seeing 2,000 / 1,800 /
            1,700 does.

            Only on All Audiences: on a single audience the equivalent evidence is the
            per-network table already sitting below, so repeating it here would be noise.
        */}
        {current.id === null && audiencesWithCounts.length > 1 && (
          <>
            <p className="mon-panel__foot">
              Each audience reported its own count. They cannot be added, because this
              campaign runs one list as several audiences, so these are the same people
              over again rather than separate groups. The largest is what Matched shows.
            </p>
            <div className="table-responsive">
              <table className="table table-sm mon-costtable mb-0">
                <thead>
                  <tr>
                    <th>Audience</th>
                    <th className="text-end">Matched</th>
                  </tr>
                </thead>
                <tbody>
                  {audiencesWithCounts.map((audience) => {
                    const isShown = audience.matched === bestAudienceMatched
                    return (
                      <tr key={audience.id ?? audience.name}>
                        <td>{audience.name}</td>
                        <td className="text-end">
                          {count(audience.matched as number)}
                          {isShown && (
                            <span className="mon-panel__tag ms-2">shown above</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </details>

      <h3 className="mon-subhead">Matched, By Network — {current.name}</h3>
      {current.networks.length === 0 ? (
        <p className="text-muted fs-11 mb-0">
          No network has reported a match for this audience yet.
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm mon-costtable mb-0">
            <thead>
              <tr>
                <th>Network</th>
                <th className="text-end">Selected</th>
                <th className="text-end">Matched</th>
                <th className="text-end">Match Rate</th>
              </tr>
            </thead>
            <tbody>
              {current.networks.map((row) => (
                <tr key={row.network}>
                  <td>{networkLabel(row.network)}</td>
                  {/* The same list goes to every network, so this does not vary, and
                      it now agrees with the Selected card above rather than counting the
                      same people once per upload. */}
                  <td className="text-end">{count(row.selected)}</td>
                  <td className="text-end">
                    {row.matched === null ? NO_VALUE : count(row.matched)}
                  </td>
                  <td className="text-end">
                    {row.match_rate === null ? NO_VALUE : `${(row.match_rate * 100).toFixed(1)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mon-panel__foot">
        Every figure here counts people. No single network can beat the Matched total,
        because that total is the best network of the best audience. The rows can still
        add up to more than it, because one person matching on several networks is
        counted in each of their rows. That is expected, not an error. A dash means the
        network has not reported a count yet, which is different from matching nobody.
      </p>
    </div>
  )
}
